import get from 'lodash/get';
import map from 'lodash/map';
import keys from 'lodash/keys';
import pick from 'lodash/pick';
import find from 'lodash/find';
import omit from 'lodash/omit';
import every from 'lodash/every';
import merge from 'lodash/merge';
import first from 'lodash/first';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import without from 'lodash/without';
import property from 'lodash/property';
import isString from 'lodash/isString';
import toString from 'lodash/toString';
import mapValues from 'lodash/mapValues';
import valuesObj from 'lodash/values';

import { createSelector } from 'reselect';
import { getParameterType, getParameterTypeByDefault, defineDatasetVarTypes } from 'utilities';

import {
  getVisibleSelectedParameters,
  getSelectedParameters,
  getVisibleNumericalSelectedParametersNames
} from '../parameters';

import {
  getNumericalSummary,
  getCategoricalSummary
} from '../summary';

import getParent from '../../selectors';
import { FREQUENCY, CONTINGENCY, RELATIVE_FREQUENCY } from 'scenes/Dataset/components/constants';

const getDatasetStore = state => getParent(state).dataset.dataset;

export const getDataset = state => getDatasetStore(state).data;
export const getDatasetName = state => getDatasetStore(state).nameOfDataset;
export const getDatasetType = state => getDatasetStore(state).typeOfDataset;
export const getCorrectVarTypes = state => getDatasetStore(state).variableTypes;

export const getDatasetLength = state => getDataset(state).length;

// Regular dataset
export const getFilteredDataset = (withAdditionalData = false) => createSelector(
  getDataset,
  getVisibleSelectedParameters,

  (dataset, parameters) => {
    const parametersWithFilter = filter(parameters, param => !!param.filter);

    const formattedDataset = map(dataset, (datasetRow, rowIndex) => {
      return merge(
        mapValues(datasetRow, (parameterValue, parameterName) => {
          const parameter = parameters[parameterName];
          const targetType = get(parameter, 'targetType');
          const shouldBeCategorical = targetType === 'categorical';
          const trimmedParameterValue = typeof parameterValue === 'string' ? parameterValue.trim() : parameterValue;
          return shouldBeCategorical && !isString(trimmedParameterValue)
            ? toString(trimmedParameterValue)
            : trimmedParameterValue;
        }), withAdditionalData ? {
          additionalData: {
            rowIndex
          }
        } : {}
      );
    });

    const compositeFilter = values =>
      every(parametersWithFilter, parameter =>
        parameter.filter(values[parameter.name])
      );

    const parametersNames = [
      ...map(parameters, property('name')),
      'additionalData'
    ];

    return map(filter(formattedDataset, compositeFilter), datasetRow =>
      pick(datasetRow, parametersNames)
    );
  }
);

export const getFilteredDatasetLength = createSelector(
  getFilteredDataset(), dataset => dataset.length
);

export const getParameters = (state, defaultTypesVariables) => {
  const selectedParams = getSelectedParameters(state);
  let variablesWithTypes = {};

  const { numericalVariable, categoricalVariable } = defaultTypesVariables;
  const dataSet = getDataset(state);
  let firstNotEmptyObj = first(filter(dataSet, datasetRow => valuesObj(datasetRow).every(param => param !== 'empty')));
  // In this if state, we handle edge case when any column is completely empty.
  // We set targetType and sourceType to the numerical by default if column is empty.
  if (!firstNotEmptyObj) {
    firstNotEmptyObj = mapValues(first(dataSet), value => value === 'empty' ? 1 : value);
  }

  if (numericalVariable && categoricalVariable) {
    const variablesWithNumericalTypes = numericalVariable.reduce((previous, current) => {
      return {
        ...previous,
        [current.trim()]: 'numerical'
      };
    }, {});
    const variablesWithNumericalAndCategoricalTypes = categoricalVariable.reduce((previous, current) => {
      return {
        ...previous,
        [current.trim()]: 'categorical'
      };
    }, variablesWithNumericalTypes);
    variablesWithTypes = mapValues(firstNotEmptyObj, getParameterTypeByDefault(variablesWithNumericalAndCategoricalTypes));
  } else {
    variablesWithTypes = defineDatasetVarTypes(dataSet);
  }

  const typeMapping = omit(variablesWithTypes, ['additionalData']);
  return {
    defaultTypes: mapValues(typeMapping, (type, name) => {
      if (selectedParams && selectedParams[name]) {
        return selectedParams[name];
      }
      return ({
        name,
        targetType: type,
        sourceType: type,
        isSelected: false
      });
    }
    ), correctTypes: defineDatasetVarTypes(dataSet)
  };
};

export const getParametersFromDataSet = dataSet => {
  let firstNotEmptyObj = first(filter(dataSet, datasetRow => valuesObj(datasetRow).every(param => param !== 'empty')));
  // In this if state, we handle edge case when any column is completely empty.
  // We set targetType and sourceType to the numerical by default if column is empty.
  if (!firstNotEmptyObj) {
    firstNotEmptyObj = mapValues(first(dataSet), value => value === 'empty' ? 1 : value);
  }
  const typeMapping = mapValues(firstNotEmptyObj, getParameterType);
  return {
    selectedParams: mapValues(typeMapping, (type, name) => ({
      name,
      targetType: type,
      sourceType: type,
      isVisible: true,
      isSelected: true
    })
    ), correctTypes: defineDatasetVarTypes(dataSet)
  };
};

export const getParameterValues = (state, parameter) =>
  map(getDataset(state), property(parameter));

export const getFilteredParameterValues = (state, parameterName) => createSelector(
  getFilteredDataset(),
  dataset => {
    if (!parameterName) {
      return null;
    }
    return map(dataset, property(parameterName));
  })(state);

export const getFilteredSelectedParametersValues = createSelector(
  getFilteredDataset(),
  getVisibleSelectedParameters,

  (filteredDataset, selectedParameters) => {
    return mapValues(selectedParameters, (_, parameterName) => {
      return filteredDataset.map(property(parameterName));
    });
  }
);

// Return an array of not-empty parameter values
// Receive: [1, 2, 'empty', 4, 'empty', '']
// Return: [1, 2, 4]
export const getFilteredParameterValuesNotEmpty = (state, parameter) => createSelector(
  elementState => getFilteredParameterValues(elementState, parameter),
  parameterValues => without(parameterValues, 'empty', '')
)(state);

// Return two arrays of non-empty values of the first and second parameters
// Receive:
//        first [11,  12, 'empty', 14, 'empty', '']
//        second [21, 'empty', 23, 24, 25, 26]
// Return:
//        first [11, 14]
//        second [21, 24]
// We need to make this calculation because we need to remove observations
// that may have empty values from two arrays at once.
export const getFilteredParametersValuesNotEmpty = (state, firstParameter, secondParameter) =>
  createSelector(
    elementState => getFilteredParameterValues(elementState, firstParameter),
    elementState => getFilteredParameterValues(elementState, secondParameter),
    (firstParameterValues, secondParameterValues) => {
      const firstParsedParameterValues = filter(firstParameterValues, (value, index) =>
        !(secondParameterValues[index] === 'empty' || value === 'empty' || secondParameterValues[index] === '' || value === ''));
      const secondParsedParameterValues = filter(secondParameterValues, (value, index) =>
        !(firstParameterValues[index] === 'empty' || value === 'empty' || firstParameterValues[index] === '' || value === ''));

      return { firstParsedParameterValues, secondParsedParameterValues };
    }
  )(state);

export const getFilteredParametersValuesNotEmptyLength = (state, firstParameter, secondParameter) => {
  if (!firstParameter && !secondParameter) {
    return 0;
  }

  if (!secondParameter) {
    return getFilteredParameterValuesNotEmpty(state, firstParameter).length;
  }

  return getFilteredParametersValuesNotEmpty(state, firstParameter, secondParameter).firstParsedParameterValues.length;
};

export const getFirstAndSecondNumericalFilteredParameterValuesNotEmpty = state => {
  const numericalSummary = getNumericalSummary(state);
  if (isEmpty(numericalSummary)) {
    return [];
  }
  const numericalSelectedParametersNames = getVisibleNumericalSelectedParametersNames(state);
  if (numericalSelectedParametersNames.length > 1) {
    const firstParameter = numericalSummary[numericalSelectedParametersNames[0]].name;
    const secondParameter = numericalSummary[numericalSelectedParametersNames[1]].name;
    return getFilteredParametersValuesNotEmpty(state, firstParameter, secondParameter);
  }
  return [];
};

export const hasChosenNumericalVariablesEmptyCells = (state, firstParameter, secondParameter) => {
  if (firstParameter === '' || secondParameter === '') {
    return false;
  }
  const row1 = getFilteredParameterValues(state, firstParameter);
  const row2 = getFilteredParameterValues(state, secondParameter);

  return (
    row1.includes('empty') || row1.includes('') ||
    row2.includes('empty') || row2.includes('')
  );
};

// frequency table
export const getFrequencyDataSet = state =>
  getDatasetStore(state).frequencyDataSet;

export const getFrequencyDataSetLength = state => {
  const frequencyDataset = getFrequencyDataSet(state);
  return frequencyDataset ? frequencyDataset.length : 0;
};

export const isFrequencyOrContingencyTable = state => {
  const type = getDatasetType(state);
  return type === FREQUENCY || type === CONTINGENCY;
};

export const getFrequencyColumnHeaders = state =>
  getDatasetStore(state).nameOfColumns;


export const getFrequencyCategoricalParametersName = state => {
  const summary = getCategoricalSummary(state);
  return keys(summary);
};

export const getFrequencyParameterType = state => nameParameter => {
  const parameter = getCategoricalSummary(state)[nameParameter];
  return parameter ? 'categorical' : '';
};

export const getLevelsRowFrequencyDatasetByName = state => name => {
  const dataset = getFrequencyDataSet(state);
  const datasetRow = find(dataset, name)[name];

  return {
    levels: datasetRow.levels,
    totalCount: datasetRow.count
  };
};

export const isRelativeFrequencyTable = state => {
  const type = getDatasetType(state);
  return type === RELATIVE_FREQUENCY;
};

export default getDataset;
